<script lang="ts">
export default {
  name: 'PolicyEditReviewPanel',
};
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { AlertTriangle, Building2, FileText, LayoutList, ArrowRight } from '@lucide/vue';
import Alert from '@/components/v2/Alert.vue';
import type { ReviewRecord, ReviewRecordRef } from '@/composables/usePolicyEditPending';

const recordTypeLabels: Record<ReviewRecordRef['type'], string> = {
  policy: 'Policy',
  line: 'Policy line',
  account: '',
};

const props = defineProps<{
  records: ReviewRecord[];
}>();

const totalFieldChanges = computed(() => props.records.reduce((sum, r) => sum + r.diffs.length, 0));
const policyChanged = computed(() => props.records.some((r) => r.ref.type === 'policy'));
const linesChanged = computed(() => props.records.filter((r) => r.ref.type === 'line').length);
</script>

<template>
  <div class="flex flex-col gap-4 p-1">
    <Alert
      type="warning"
      :icon="AlertTriangle"
      class="text-sm">
      Saving will update benchmarking, carrier analytics, and client-facing data across the platform. Review all changes before confirming.
    </Alert>

    <div class="flex flex-wrap gap-2">
      <span class="badge badge-outline border-base-300 badge-md text-slate-medium">
        <strong class="mr-1 text-slate-dark">{{ totalFieldChanges }}</strong> field changes
      </span>
      <span class="badge badge-outline border-base-300 badge-md text-slate-medium">
        <strong class="mr-1 text-slate-dark">{{ policyChanged ? 1 : 0 }}</strong> policy record
      </span>
      <span class="badge badge-outline border-base-300 badge-md text-slate-medium">
        <strong class="mr-1 text-slate-dark">{{ linesChanged }}</strong> lines affected
      </span>
    </div>

    <div
      v-if="records.length === 0"
      class="rounded border border-base-300 p-6 text-center text-sm text-slate-medium">
      No pending changes.
    </div>

    <div
      v-for="record in records"
      :key="record.ref.key"
      class="overflow-hidden rounded-lg border border-base-300">
      <div class="flex items-center gap-3 border-b border-base-300 bg-base-200 px-4 py-2">
        <div
          class="flex h-7 w-7 items-center justify-center rounded"
          :class="record.ref.type === 'policy' ? 'bg-primary/10 text-primary' : 'bg-base-300 text-slate-medium'">
          <FileText
            v-if="record.ref.type === 'policy'"
            class="h-4 w-4" />
          <Building2
            v-else-if="record.ref.type === 'account'"
            class="h-4 w-4" />
          <LayoutList
            v-else
            class="h-4 w-4" />
        </div>
        <div class="flex-1 text-sm font-semibold text-slate-dark">
          {{ record.ref.label }}
          <span
            v-if="recordTypeLabels[record.ref.type]"
            class="ml-1 text-xs font-normal text-slate-medium"
            >{{ recordTypeLabels[record.ref.type] }}</span
          >
        </div>
        <span class="badge border-0 bg-primary/10 badge-sm text-primary">
          {{ record.diffs.length }} {{ record.diffs.length === 1 ? 'change' : 'changes' }}
        </span>
      </div>
      <table class="w-full table-fixed">
        <thead>
          <tr class="border-b border-base-300 bg-base-200 text-left text-xs tracking-wide text-slate-medium uppercase">
            <th class="w-2/5 px-4 py-2 font-semibold">Field</th>
            <th class="w-1/5 px-4 py-2 font-semibold">Current</th>
            <th class="w-8 px-2 py-2"></th>
            <th class="px-4 py-2 font-semibold">New</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="diff in record.diffs"
            :key="diff.field"
            class="border-b border-base-200 text-sm last:border-b-0"
            :class="{ 'opacity-60': diff.restricted }">
            <td class="px-4 py-2 text-slate-dark">
              <span :class="{ 'line-through': diff.restricted }">{{ diff.label }}</span>
              <div
                v-if="diff.restricted && diff.restrictionReason"
                class="mt-0.5 flex items-center gap-1 text-[11px] text-amber-700">
                <AlertTriangle class="h-3 w-3" />
                <span>{{ diff.restrictionReason }}</span>
              </div>
            </td>
            <td class="px-4 py-2">
              <span
                v-if="diff.oldValue"
                class="text-error"
                :class="{ 'line-through': diff.restricted }"
                >{{ diff.oldValue }}</span
              >
              <span
                v-else
                class="text-xs text-slate-light italic"
                >—</span
              >
            </td>
            <td class="px-2 py-2 text-center text-slate-light">
              <ArrowRight class="inline-block h-3.5 w-3.5" />
            </td>
            <td class="px-4 py-2">
              <span
                v-if="diff.newValue"
                class="font-medium text-success"
                :class="{ 'line-through': diff.restricted }"
                >{{ diff.newValue }}</span
              >
              <span
                v-else
                class="text-xs text-slate-light italic"
                >—</span
              >
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
